import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
// External components
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Box, Button, Divider, Grid, Typography, Card } from "@mui/material";
// Graphql
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { client_EB_api } from "../../graphql/client";
import { GET_BILL_BY_ID, GET_EB_BY_ID, RESEND_EMAIL } from "../../graphql";
import { UPDATE_CONFIGURATION_BILL } from "../../graphql/billConfiguration/billConfiguration.mutation";
// Components
import OptionCard from "../OptionCard";
import NotesForm from "../NotesForm";
import Loading from "../Loading";
import ResumeCard from "../ResumeCard";
import ClientCard from "../ClientCard";
import PaymentDataCard from "../PaymentDataCard";
import CustomLoading from "../CustomLoading";
import SendBillScreen from "../SendBillScreen";
import ScheduledBillCard from "../ScheduledBillCard/ScheduledBillCard";
import RegisterReceiver from "../RegisterReceiver";
import ActivityCodeSelect from "../ActivityCodeSelect/ActivityCodeSelect";
// Hooks
import useElectronicBill from "../../hooks/useElectronicBill";
import useCreditNote from "../../hooks/useCreditNote";
import useDebitNote from "../../hooks/useDebitNote";
import useUsers from "../../hooks/useUsers";
// Context
import { useAuth, useModal } from "../../context";
import { useNotifications } from "../../context/NotificationsContext";
import { useElectronic } from "../../context/ElectronicContext";
// Enums
import {
  BILL_TYPES_URL_PARAMS,
  ElectronicBillTypes,
  ELECTRONIC_BILLS_DOCUMENT_TYPES,
} from "../../Enums/ElectronicBill";
import { ElectronicBillErrors, UserErrors } from "../../Enums/Errors";
// Utils
import { customToast, customToast as toast } from "../../utils";
import { billConfigurationEmptyState } from "../../InitialStates/ElectronicContext";
import { getIsUserRegisteredInHacienda } from "../../utils/helpers";
import {
  selectProperBillingProfileFromPlace,
  placeHasValidBillingProfile,
} from "../../utils/placeUtils";
import { billingProfileHasValidMHData } from "../../utils/billingProfileUtils";
// Icons
import addProductIcon from "../../assets/icons/add_product_icon.svg";
import LineDetailProductCard from "../LineDetailProductCard";

const submitText = {
  CreditNote: "Enviar Nota de crédito",
  DebitNote: "Enviar nota de débito",
};

function PaymentComponent() {
  const [noteInfo, setNoteInfo] = useState({ code: "", reason: "" });
  const [sendingBill, setSendingBill] = useState(null);
  const [registeredTicket, setRegisteredTicket] = useState(false);
  const [sentDraft, setSentDraft] = useState(false);
  const [loadState, setLoadState] = useState({
    loadingState: "loading",
    isLoading: false,
  });
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { id, billType } = useParams();
  const { saveCreditNote, canSaveCN } = useCreditNote();
  const { canSaveDN, saveDebitNote } = useDebitNote();
  const {
    saveTempralBill: saveTemporalBill,
    saveEBillFormBill,
    loadElectronicBill,
    loadDraftBill,
  } = useElectronicBill();

  const client = useApolloClient();

  const {
    lineDetail,
    LineDetailPlace,
    receiver,
    electronicBill,
    setElectronicBill,
    setLineDetail,
    useCoinDetail,
    isBilling,
    setIsBilling,
    resetElectronicBill,
    setScheduledBill,
    scheduledBill,
    setReceiver,
    setAddress,
    setClientGroup,
  } = useElectronic();

  const { addNewClient, dataClientsGroups, createGroup, refetchGroups } =
    useUsers();
  const { resetDefaultCoin } = useCoinDetail;
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const { haveActionPermission, noAccessMessage } = permissions;
  const { setModalOpen } = useModal();
  const { createNotification, refetchNotifications, setNotifications } =
    useNotifications();
  const [updateConfigurationBill] = useMutation(UPDATE_CONFIGURATION_BILL);
  const [submitInvoiceText, setSubmitInvoiceText] = useState(
    "Crear tiquete Electrónico",
  );

  const canRead = haveActionPermission(
    "Read",
    "/electronic-bills",
    LineDetailPlace,
  );
  if (!canRead) {
    toast.warning(noAccessMessage("Leer", "Fac Electrónica"));
    navigate(`/`);
  }
  const {
    data: dataBill,
    loading,
    refetch,
  } = useQuery(GET_BILL_BY_ID, {
    variables: {
      id: sendingBill?.id || id,
    },
    skip: !id,
  });

  const [billConfiguration, setBillConfiguration] = useState({
    FK_Bill: Number(id),
    date: dayjs(),
    time: dayjs(),
    Day: dayjs().day(),
    Month: dayjs().month() + 1,
    Year: dayjs().year(),
    Hour: dayjs().utc().hour(),
    Minute: dayjs().minute(),
    Frequency: 1,
    Active: 0,
    Next_Execution: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
  });

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");

  const isCreditNoteView = useMemo(
    () => billType === BILL_TYPES_URL_PARAMS.CREDIT_NOTE,
    [billType],
  );
  const isDebitNoteView = useMemo(
    () => billType === BILL_TYPES_URL_PARAMS.DEBIT_NOTE,
    [billType],
  );
  const isNote = useMemo(
    () => isCreditNoteView || isDebitNoteView,
    [isDebitNoteView, isCreditNoteView],
  );

  const isDraft = useMemo(
    () => billType === BILL_TYPES_URL_PARAMS.DRAFT,
    [billType],
  );

  const handleSaveAsTemp = async () => {
    if (!placeHasValidBillingProfile(LineDetailPlace)) {
      customToast.error(
        "Perfil de facturación inválido. Verifíquelo antes de volverlo a intentar",
      );
      return;
    }
    if (
      !billingProfileHasValidMHData(
        selectProperBillingProfileFromPlace(LineDetailPlace),
      )
    ) {
      customToast.error(
        "Datos de Hacienda inválidos. Verifíquelos antes de volverlo a intentar",
      );
      return;
    }
    if (lineDetail.length === 0) return;
    const bill = await saveTemporalBill();

    setElectronicBill(prev => ({ ...prev, id: bill.id }));
    setLineDetail(prev =>
      prev.map(detail => ({
        ...detail,
        detail_id: bill.Detail.find(
          _detail => _detail?.ProductFeature?.id === detail?.id,
        )?.id,
      })),
    );
    setSentDraft(true);
  };

  const handleActivityChange = value => {
    setElectronicBill({
      ...electronicBill,
      activity_emitter: value,
    });
  };

  const resetBill = useCallback(() => {
    resetElectronicBill();
    resetDefaultCoin();
    if (!isNote) {
      setLineDetail([]);
    }
  }, [resetDefaultCoin, resetElectronicBill, setLineDetail, isNote]);

  const getSaveButtonText = useMemo(
    () =>
      electronicBill.id
        ? `La factura será guardada automáticamente`
        : "Guardar en borrador",
    [electronicBill.id],
  );

  const ToastsFactory = error => {
    switch (error.message) {
      case UserErrors.EMPTY_CHECKED_PRODUCTS:
        toast.error("Seleccione al menos un producto");
        break;
      default:
        toast.error("Error desconocido");
    }
  };

  const sendCreditNote = async () => {
    try {
      const validation = canSaveCN();
      if (validation.hasError) {
        ToastsFactory(validation);
        return;
      }
      const {
        data: { createCreditNote },
      } = await saveCreditNote(noteInfo);
      if (createCreditNote?.error === ElectronicBillErrors.ACCESS_TOKEN) {
        toast.error("Error al registrar el tiquete, revise sus credenciales");
        resetBill();
        await handleCreateNotification(
          "Error - Nota de Crédito",
          "Error al registrar el tiquete, revise sus credenciales",
          "",
          id ? `/electronic-bills/${id}/Draft` : "/electronic-bills/",
        );
        setLoadState(prev => ({ loadingState: "error", isLoading: false }));
        return;
      }
      toast.success("¡Nota de Crédito guardada con éxito!");
      await handleCreateNotification(
        "Exito - Nota de Crédito",
        "¡Nota de Crédito guardada con éxito!",
        "",
        `/ElectronicBillView/${createCreditNote.id}/created/${BILL_TYPES_URL_PARAMS.CREDIT_NOTE}`,
      );
      navigate(
        `/ElectronicBillView/${createCreditNote.id}/created/${BILL_TYPES_URL_PARAMS.CREDIT_NOTE}`,
      );
    } catch {
      toast.error("Ha ocurrido un error");
      await handleCreateNotification(
        "Error - Nota de Crédito",
        "Ha ocurrido un error al enviar la nota de crédito",
        "",
        id ? `/electronic-bills/${id}/CreditNote` : "/electronic-bills/",
      );
    }
  };

  const sendDebitNote = async () => {
    try {
      const validation = canSaveDN();
      if (validation.hasError) {
        ToastsFactory(validation);
        return;
      }
      const {
        data: { createDebitNote },
      } = await saveDebitNote(noteInfo);
      if (createDebitNote?.error === ElectronicBillErrors.ACCESS_TOKEN) {
        toast.error("Error al registrar el tiquete, revise sus credenciales");
        resetBill();
        setLoadState(prev => ({ loadingState: "error", isLoading: false }));
        return;
      }
      toast.success("¡Nota de Débito guardada con éxito!");
      navigate(
        `/ElectronicBillView/${createDebitNote.id}/created/${BILL_TYPES_URL_PARAMS.DEBIT_NOTE}`,
      );
    } catch {
      toast.error("Ha ocurrido un error");
    }
  };

  const handleSendBill = async () => {
    try {
      if (!placeHasValidBillingProfile(LineDetailPlace)) {
        customToast.error(
          "Perfil de facturación inválido. Verifíquelo antes de volverlo a intentar",
        );
        return;
      }
      if (
        !billingProfileHasValidMHData(
          selectProperBillingProfileFromPlace(LineDetailPlace),
        )
      ) {
        customToast.error(
          "Datos de Hacienda inválidos. Verifíquelos antes de volverlo a intentar",
        );
        return;
      }
      setLoadState(prev => ({ loadingState: "loading", isLoading: true }));
      if (billType === BILL_TYPES_URL_PARAMS.CREDIT_NOTE) {
        sendCreditNote();
        return;
      }
      if (billType === BILL_TYPES_URL_PARAMS.DEBIT_NOTE) {
        sendDebitNote();
        return;
      }
      if (!billType || isDraft) {
        const bill = !isDraft ? await saveTemporalBill() : electronicBill;
        const resp = await saveEBillFormBill(bill.id);
        if (resp.error) {
          handleShowError(resp.error);
          await handleCreateNotification(
            "Error - Factura",
            "Ha ocurrido un error al intentar registrar la factura",
            "",
            bill?.id
              ? `/electronic-bills/${bill.id}/Draft`
              : "/electronic-bills",
          );
          return;
        }
        if (bill && resp.id) {
          setSendingBill(bill);
          toast.success("¡Factura  guardada con éxito!");
          handleCreateNotification(
            "Éxito - Factura",
            "¡Factura Guardada Exitosamente!",
            "",
            `/ElectronicBillView/${resp.id}/created/${BILL_TYPES_URL_PARAMS.ELECTRONIC_BILL}}`,
          );
          navigate(
            redirectTo ??
              `/ElectronicBillView/${resp.id}/created/${BILL_TYPES_URL_PARAMS.ELECTRONIC_BILL}`,
          );
        }
        setRegisteredTicket(true);
      }
    } catch (err) {
      toast.error("Error al registrar el tiquete");
      await handleCreateNotification(
        "Error - Factura",
        "Error al registrar el tiquete",
        "",
        "/electronic-bills",
      );
      resetBill();
    }
    setLoadState(prev => ({ ...prev, isLoading: false }));
  };

  const getBill = async idEB => {
    const bill = await client.query({
      query: GET_EB_BY_ID,
      variables: {
        id: idEB,
      },
    });

    return bill;
  };

  const handleSendEmails = async receivers => {
    let idElectronicBill = 0;
    let features = [];

    if (billType !== BILL_TYPES_URL_PARAMS.DRAFT) {
      let bill = await getBill(electronicBill.id);
      idElectronicBill = bill?.data?.EBill?.Bill?.id;

      if (
        bill?.data?.EBill?.ElectronicBillDocumentType?.id ===
          ELECTRONIC_BILLS_DOCUMENT_TYPES.CREDIT_NOTE.toString() ||
        bill?.data?.EBill?.ElectronicBillDocumentType?.id ===
          ELECTRONIC_BILLS_DOCUMENT_TYPES.DEBIT_NOTE.toString()
      ) {
        bill = await getBill(bill?.data?.EBill?.ReferencedDocument?.id);
        idElectronicBill = bill?.data?.EBill?.Bill?.id;
        bill?.data?.EBill?.ElectronicBillDetail.map(detail =>
          features.push(detail.id),
        );
      }
    } else {
      features = lineDetail.map(({ value }) => value);
    }

    await client_EB_api.mutate({
      mutation: RESEND_EMAIL,
      variables: {
        id:
          billType === BILL_TYPES_URL_PARAMS.DRAFT || sentDraft
            ? electronicBill.id
            : idElectronicBill,
        idElectronicBillFeatures: features,
        to: receivers,
      },
    });
    toast.success("Email enviado con éxito!");
  };

  const openSendEmailModal = () => {
    setModalOpen(
      true,
      <SendBillScreen
        receptorRequired
        billingEmail={receiver?.Receiver_Email}
        handleCloseModal={() => setModalOpen(false)}
        handleSend={handleSendEmails}
        place={LineDetailPlace}
      />,
    );
  };

  const handleShowError = error => {
    if (error === ElectronicBillErrors.ACCESS_TOKEN) {
      toast.error("Error al registrar el tiquete, revise sus credenciales");
      setLoadState(prev => ({ loadingState: "error", isLoading: false }));
      navigate("/electronic-bills", { replace: true });
    }
  };

  const handleCreateNotification = async (title, body, icon, clickAction) => {
    await createNotification(title, body, icon, clickAction);
    setNotifications(await refetchNotifications());
  };

  const getType = () => {
    if (isNote)
      return isDebitNoteView
        ? ElectronicBillTypes.DebitNote
        : ElectronicBillTypes.CreditNote;
    return ElectronicBillTypes.FacturaElectronica;
  };

  const availableSendDocument = () => {
    const { code, reason } = noteInfo;

    const selected =
      Array.isArray(lineDetail) && lineDetail.length > 0 ? lineDetail : [];

    if ((!code || !reason || !selected.length) && isNote) {
      return true;
    }
    if (!isNote) {
      return !lineDetail.length;
    }

    return false;
  };

  const handleNextExecution = (frequency, date) => {
    const formatFrequency = frequency.toString();
    const currentDay = dayjs().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    let formatNextExecution;

    if (formatFrequency === "1") {
      formatNextExecution = dayjs(date).add(1, "day");
    }
    if (formatFrequency === "2") {
      formatNextExecution = dayjs(date).add(7, "day");
    }
    if (formatFrequency === "3") {
      formatNextExecution = dayjs(date).add(1, "month");
    }
    if (date > currentDay) {
      formatNextExecution = dayjs(date)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    return formatNextExecution;
  };

  const roundToNext15Minutes = date => {
    const minutes = dayjs(date).minute();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    return dayjs(date).minute(roundedMinutes).second(0);
  };

  const updateValues = (name, value) => {
    setBillConfiguration({ ...billConfiguration, [name]: value });

    const newBillConfiguration = { ...billConfiguration, [name]: value };

    const newDate = name === "date" ? value : billConfiguration?.date;

    const validatedExecution = handleNextExecution(
      newBillConfiguration.Frequency,
      dayjs(newDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
    );

    const dataWithFormat = {
      FK_Bill: Number(id),
      date:
        name === "date"
          ? roundToNext15Minutes(newDate)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
          : roundToNext15Minutes(dayjs().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")),
      Day: roundToNext15Minutes(newDate).date(),
      Month: roundToNext15Minutes(newDate).month() + 1,
      Year: roundToNext15Minutes(newDate).year(),
      Hour: roundToNext15Minutes(newDate).utc().hour(),
      Minute: roundToNext15Minutes(newDate).minute(),
      Frequency: newBillConfiguration.Frequency,
      Active: newBillConfiguration.Active,
      Next_Execution: validatedExecution,
    };

    if (id) {
      updateConfigurationBill({
        variables: {
          billConfiguration: {
            ...dataWithFormat,
          },
        },
      })
        .then(response => {
          refetch();
          toast.success("Cambio exitoso");
        })
        .catch(error => {
          toast.error("Error al programar la factura");
        });
    }
  };

  const handleOpenModal = () => {
    setModalOpen(
      true,
      <Box
        className="modal-content-size container-modal "
        sx={{
          height: { xs: "600px", sm: "100%" },
          width: { xs: "333px", sm: "100%" },
        }}
      >
        <RegisterReceiver
          newClient
          FK_User={0}
          handleChangeAddress={() => null}
          handleCreateNewClient={addNewClientAddapter}
          handleOnClosed={() => setModalOpen(false)}
          initialReceiver={null}
          setBillingProfile={null}
          title="Perfil de facturación"
        />
      </Box>,
    );
  };

  const addNewClientAddapter = async profile => {
    const providersGroup = dataClientsGroups?.groups?.find(
      ({ Name_ClientGroup }) => Name_ClientGroup === "Clientes",
    );
    const selectedGroup =
      providersGroup?.id ||
      (
        await createGroup({
          Name_ClientGroup: "Clientes",
          FK_Place: LineDetailPlace.id,
        })
      )?.clientGroup?.id;
    refetchGroups();
    if (!selectedGroup) {
      return toast.error("Ocurrió un error al crear el grupo");
    }

    const newClient = await addNewClient(profile, selectedGroup, "Cliente");
    const clientData = newClient?.data?.client;
    const billingProfile = {
      id: clientData?.BillingProfile?.id,
      Receiver_Id: clientData?.BillingProfile?.ID_Number,
      Receiver_Name: clientData?.BillingProfile?.Name,
      Receiver_Email: clientData?.BillingProfile?.Email,
      Receiver_PhoneCode: clientData?.BillingProfile?.PhoneCodCountry,
      Receiver_PhoneNumber: clientData?.BillingProfile?.PhoneNumber,
      Receiver_PersonType: clientData?.BillingProfile?.MH_PersonType,
      user: { id: clientData?.BillingProfile?.User.id },
    };
    const address = {
      Province:
        clientData?.BillingProfile?.MH_Neighborhood?.District?.Canton?.Province,
      Canton: clientData?.BillingProfile?.MH_Neighborhood?.District?.Canton,
      District: clientData?.BillingProfile?.MH_Neighborhood?.District,
      Neighborhood: clientData?.BillingProfile?.MH_Neighborhood,
      Other: clientData?.BillingProfile?.OtherSigns,
    };

    setAddress(address);
    setClientGroup({ id: 0 });
    setModalOpen(false);

    return setReceiver(billingProfile);
  };

  useEffect(() => {
    const fetchText = async () => {
      if (billType && !isDraft) {
        setSubmitInvoiceText(submitText[billType]);
        return;
      }

      const isATV = await getIsUserRegisteredInHacienda(receiver.Receiver_Id);
      if (isATV) {
        setSubmitInvoiceText("Factura Electrónica");
        return;
      }
      setSubmitInvoiceText("Crear tiquete Electrónico");
    };

    fetchText();
  }, [billType, isDraft, receiver.Receiver_Id]);
  // this needs improvements
  useEffect(
    () => {
      if (!electronicBill.activity_emitter) {
        setElectronicBill({
          ...electronicBill,
          activity_emitter: {
            label: LineDetailPlace.Activity?.Name,
            code: LineDetailPlace.Activity?.id_MH,
            value: LineDetailPlace.Activity?.id,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => {
    if (!isBilling) resetBill();
    if (!id) {
      setScheduledBill(billConfigurationEmptyState);
      return;
    }
    if (isDraft) {
      loadDraftBill(id);
      return;
    }
    loadElectronicBill(id, isNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(
    () => {
      if (isBilling) setIsBilling(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBilling],
  );
  // UPDATE DATA IF EXISTS
  useEffect(() => {
    if (dataBill?.bill?.BillConfiguration) {
      const configuration = dataBill?.bill?.BillConfiguration;

      setBillConfiguration({
        FK_Bill: Number(id),
        date: dayjs(configuration.date)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        Day: dayjs(configuration?.Day).day(),
        Month: dayjs(configuration?.Month).month() + 1,
        Year: dayjs(configuration?.Year).year(),
        Hour: dayjs(configuration?.Hour).hour(),
        Minute: dayjs(configuration?.Minute).minute(),
        Frequency: configuration?.Frequency,
        Active: configuration?.Active,
        Next_Execution: dayjs(configuration.date)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataBill]);

  if (loadState.isLoading)
    return (
      <CustomLoading
        centered
        vCentered
        errorTexts={["Algo ha fallado"]}
        loadingState={loadState.loadingState}
        loadingTexts={["Estamos enviando su factura", "Por favor, espere..."]}
      />
    );

  if (loading)
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Loading />
      </Box>
    );

  return (
    <Grid container spacing={2}>
      {scheduledBill?.Active && (
        <Grid item xs={12}>
          <ScheduledBillCard
            data={billConfiguration}
            id={id}
            setBillConfiguration={setBillConfiguration}
            updateValues={updateValues}
          />
        </Grid>
      )}
      <Grid item sm={6} xs={12}>
        <ClientCard
          data={{
            Id: receiver?.Receiver_Id,
            Name: receiver?.Receiver_Name,
            Email: receiver?.Receiver_Email,
            PhoneNumber: receiver?.Receiver_PhoneNumber,
            activity_receiver: {
              code:
                electronicBill?.ReceiverActivity?.id_MH ??
                electronicBill?.activity_receiver?.code,
            },
          }}
          disabled={isNote}
          optional2={
            receiver?.Receiver_Email === "" &&
            receiver?.Receiver_PhoneNumber === ""
              ? ""
              : ` ${receiver?.Receiver_Email}, 
                      ${receiver?.Receiver_PhoneNumber}`
          }
          sx={{
            height: "100%",
          }}
          type={(() => {
            if (isNote) {
              return !isCreditNoteView
                ? ElectronicBillTypes.DebitNote
                : ElectronicBillTypes.CreditNote;
            }
            return ElectronicBillTypes.FacturaElectronica;
          })()}
          onActivityCodeChange={value => {
            setElectronicBill({
              ...electronicBill,
              activity_receiver: value,
            });
          }}
          onClickSubmit={() => handleOpenModal()}
        />
      </Grid>
      <Grid item sm={6} xs={12}>
        <PaymentDataCard
          type={(() => {
            if (isNote) {
              return !isCreditNoteView
                ? ElectronicBillTypes.DebitNote
                : ElectronicBillTypes.CreditNote;
            }
            return ElectronicBillTypes.FacturaElectronica;
          })()}
        />
      </Grid>
      {isNote && (
        <Grid item xs={12}>
          <NotesForm
            code_name="code"
            noteInfo={noteInfo}
            reason_name="reason"
            onChange={setNoteInfo}
          />
        </Grid>
      )}
      <Grid item sm={6} xs={12}>
        <Card
          color="primary"
          sx={{
            cursor: "pointer",
            height: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "1rem",
            width: "100%",
          }}
        >
          <Typography
            fontWeight={600}
            typography="modeColor"
            variant="h6"
            width="100%"
          >
            Actividad económica de la tienda
          </Typography>
          <ActivityCodeSelect
            defaultCode={
              electronicBill?.EmitterActivity?.id_MH ??
              electronicBill?.activity_emitter?.code ??
              LineDetailPlace.Activity?.id_MH
            }
            disabled={isNote}
            handleChange={handleActivityChange}
          />
        </Card>
      </Grid>
      <Grid item sm={6} xs={12}>
        <OptionCard
          mainComponent
          disabled={isCreditNoteView}
          icon={addProductIcon}
          title="Añadir productos"
        />
      </Grid>

      <Grid item xs={12}>
        {Array.isArray(lineDetail) && lineDetail?.length !== 0 ? (
          lineDetail?.map(product => (
            <Card
              color="primary"
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: { sx: "1rem", md: "2rem", xl: "3rem" },
                gap: "1rem",
                overflow: "auto",
              }}
            >
              <LineDetailProductCard
                key={product.id}
                lineDetail={lineDetail}
                product={product}
                selectables={
                  getType() === ElectronicBillTypes.DebitNote ||
                  getType() === ElectronicBillTypes.CreditNote
                }
                setLineDetail={setLineDetail}
                visualizationType={getType()}
              />
            </Card>
          ))
        ) : (
          <Box
            sx={{
              marginX: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: 700, opacity: 0.5 }}>
              No se encuentran productos/servicios registrados
            </Typography>

            <Divider color="primary" />
          </Box>
        )}
      </Grid>

      <Grid item xs={12}>
        <ResumeCard type={getType()} />
      </Grid>
      <Grid
        item
        sx={{ display: "flex", gap: 3, justifyContent: "end" }}
        xs={12}
      >
        {isNote || (
          <Button
            disabled={!lineDetail.length || electronicBill.id}
            size="medium"
            variant="contained"
            onClick={() => handleSaveAsTemp()}
          >
            {getSaveButtonText}
          </Button>
        )}
        {!registeredTicket ? (
          <Button
            disabled={availableSendDocument()}
            size="medium"
            variant="contained"
            onClick={() => handleSendBill()}
          >
            {submitInvoiceText}
          </Button>
        ) : (
          <Button
            color="light"
            disabled={!lineDetail.length}
            size="medium"
            variant="contained"
            onClick={() => navigate("/CreditDebitNote")}
          >
            Crear nota de crédito
          </Button>
        )}
        {electronicBill.id && !isNote && (
          <Button
            color="light"
            size="medium"
            variant="contained"
            onClick={() => openSendEmailModal()}
          >
            Enviar Correo
          </Button>
        )}
        <Button
          color="secondary"
          size="medium"
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
      </Grid>
    </Grid>
  );
}
export default PaymentComponent;
